import React, { useEffect } from 'react';
import { X, Sun, Sliders, Navigation, TrendingUp, Star, Check, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SunFilterConfig, SunPeriod } from '../../types';

export interface SpotFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Sortering
  sortByDistance: boolean;
  onToggleSortByDistance: () => void;
  sortByPopularity: boolean;
  onToggleSortByPopularity: () => void;
  // Soljakt
  sunFilterConfig: SunFilterConfig;
  onUpdateSunFilterConfig: (config: SunFilterConfig) => void;
  // Egenskapsfiltre
  activeFilters: Set<string>;
  onToggleFilter: (filterId: string) => void;
  onClearFilters: () => void;
  isLoggedIn: boolean;
  matchCount: number;
}

export const SpotFilterModal: React.FC<SpotFilterModalProps> = ({
  isOpen,
  onClose,
  sortByDistance,
  onToggleSortByDistance,
  sortByPopularity,
  onToggleSortByPopularity,
  sunFilterConfig,
  onUpdateSunFilterConfig,
  activeFilters,
  onToggleFilter,
  onClearFilters,
  isLoggedIn,
  matchCount,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const totalActiveCount = activeFilters.size + (sunFilterConfig.enabled ? 1 : 0) + (sortByDistance || sortByPopularity ? 1 : 0);

  const filterOptions = [
    { id: 'beste', label: t('sidebar.filters.best', 'Beste plasser (Topp-tier)'), emoji: '⭐', desc: t('sidebar.filters.best_help', 'De høyest vurderte plassene') },
    { id: 'fri_tilgang', label: t('sidebar.filters.free_access', 'Fri tilgang'), emoji: '🔓', desc: t('sidebar.filters.free_access_desc', 'Ingen bom eller adkomstavgift') },
    { id: 'verifisert', label: t('sidebar.filters.verified', 'Verifisert'), emoji: '✅', desc: t('sidebar.filters.verified_desc', 'Sjekket og bekreftet av Fricamp') },
    { id: 'toalett', label: t('sidebar.filters.toilet', 'Toalett / WC'), emoji: '🚽', desc: t('sidebar.filters.toilet_desc', 'Toalettfasiliteter tilgjengelig') },
    { id: 'strand', label: t('sidebar.filters.strand', 'Strand / Badevann'), emoji: '🌊', desc: t('sidebar.filters.strand_desc', 'Nærhet til kyst, innsjø eller strand') },
    { id: 'god_dekning', label: t('sidebar.filters.coverage', 'God mobildekning'), emoji: '📶', desc: t('sidebar.filters.coverage_desc', 'Sterk 4G/5G dekning') },
    ...(isLoggedIn ? [{ id: 'ikke_besøkt', label: t('sidebar.filters.not_visited', 'Ikke besøkt'), emoji: '👁️', desc: t('sidebar.filters.not_visited_desc', 'Skjul plasser du har sjekket inn på') }] : []),
  ];

  return (
    <div className="fixed inset-0 z-[6000] flex flex-col justify-end md:justify-center md:items-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="fixed inset-0 -z-10" 
        onClick={onClose} 
        aria-hidden="true" 
      />

      <div className="w-full md:max-w-lg bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-t-3xl md:rounded-3xl shadow-2xl flex flex-col max-h-[85vh] md:max-h-[80vh] overflow-hidden animate-in slide-in-from-bottom duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-[var(--border-primary)] shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-black text-[var(--text-primary)] uppercase tracking-wide">
                {t('sidebar.filters.title', 'Filter & Sortering')}
              </h3>
              {totalActiveCount > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-brand/20 text-emerald-400 text-3xs font-black">
                  {totalActiveCount} aktive
                </span>
              )}
            </div>
            <p className="text-2xs text-[var(--text-muted)] mt-0.5">
              {t('sidebar.filters.subtitle', 'Tilpass plassene som vises på kartet og i listen')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition-colors"
            aria-label={t('common.close', 'Lukk')}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5 custom-scrollbar">

          {/* Sortering */}
          <div>
            <span className="text-3xs font-extrabold uppercase tracking-widest text-[var(--text-muted)] block mb-2">
              {t('sidebar.sort_by', 'Sorter etter')}
            </span>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={onToggleSortByDistance}
                className={`p-3 rounded-2xl border text-left flex items-center justify-between transition-all ${
                  sortByDistance && !sunFilterConfig.enabled
                    ? 'bg-brand/15 border-brand/50 text-emerald-400 shadow-sm'
                    : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] text-[var(--text-secondary)] hover:border-[var(--border-secondary)]'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Navigation size={16} className={sortByDistance && !sunFilterConfig.enabled ? 'text-emerald-400' : 'text-[var(--text-muted)]'} />
                  <span className="text-xs font-bold truncate">{t('sidebar.distance', 'Nærmest meg')}</span>
                </div>
                {sortByDistance && !sunFilterConfig.enabled && <Check size={14} className="text-emerald-400 shrink-0" />}
              </button>

              <button
                type="button"
                onClick={onToggleSortByPopularity}
                className={`p-3 rounded-2xl border text-left flex items-center justify-between transition-all ${
                  sortByPopularity
                    ? 'bg-amber-500/15 border-amber-500/50 text-amber-300 shadow-sm'
                    : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] text-[var(--text-secondary)] hover:border-[var(--border-secondary)]'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <TrendingUp size={16} className={sortByPopularity ? 'text-amber-400' : 'text-[var(--text-muted)]'} />
                  <span className="text-xs font-bold truncate">{t('sidebar.popularity', 'Mest populære')}</span>
                </div>
                {sortByPopularity && <Check size={14} className="text-amber-400 shrink-0" />}
              </button>
            </div>
          </div>

          {/* Soljakt (Værfilter) */}
          <div className={`p-4 rounded-2xl border transition-all ${
            sunFilterConfig.enabled
              ? 'bg-amber-500/10 border-amber-500/40 shadow-sm'
              : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)]'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                  sunFilterConfig.enabled ? 'bg-amber-500/20 text-amber-400' : 'bg-[var(--bg-input)] text-[var(--text-muted)]'
                }`}>
                  <Sun size={18} className={sunFilterConfig.enabled ? 'fill-amber-400/30' : ''} />
                </div>
                <div>
                  <h4 className="text-xs font-black text-[var(--text-primary)]">
                    {t('sidebar.sun_filter.title', 'Soljakt (Værvarsel)')}
                  </h4>
                  <p className="text-3xs text-[var(--text-muted)]">
                    {t('sidebar.sun_filter.desc', 'Finn plasser med sol eller oppholdsvær')}
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={sunFilterConfig.enabled}
                  onChange={(e) => onUpdateSunFilterConfig({ ...sunFilterConfig, enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-10 h-6 bg-[var(--bg-input)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500 border border-[var(--border-secondary)]"></div>
              </label>
            </div>

            {sunFilterConfig.enabled && (
              <div className="mt-3 pt-3 border-t border-amber-500/20 space-y-3">
                <div>
                  <span className="text-4xs font-black uppercase tracking-widest text-amber-300/80 block mb-1.5">
                    {t('sidebar.sun_filter.period', 'Periode')}
                  </span>
                  <div className="grid grid-cols-4 gap-1">
                    {(['today', 'tomorrow', 'weekend', '3days'] as SunPeriod[]).map((p) => {
                      const labels: Record<SunPeriod, string> = {
                        today: t('weather.today', 'I dag'),
                        tomorrow: t('weather.tomorrow', 'I morgen'),
                        weekend: t('weather.weekend', 'Helgen'),
                        '3days': t('weather.three_days', '3 dager'),
                      };
                      const isSel = sunFilterConfig.period === p;
                      return (
                        <button
                          key={p}
                          type="button"
                          onClick={() => onUpdateSunFilterConfig({ ...sunFilterConfig, period: p })}
                          className={`py-1.5 px-2 rounded-xl text-3xs font-bold transition-colors ${
                            isSel
                              ? 'bg-amber-500 text-stone-900 shadow-sm'
                              : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] border border-[var(--border-primary)] hover:border-amber-500/30'
                          }`}
                        >
                          {labels[p]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-4xs font-black uppercase tracking-widest text-amber-300/80">
                      {t('sidebar.sun_filter.max_distance', 'Maksimal reiseavstand')}
                    </span>
                    <span className="text-3xs font-extrabold text-amber-300">
                      {sunFilterConfig.maxDistanceKm > 0 ? `${sunFilterConfig.maxDistanceKm} km` : t('sidebar.sun_filter.unlimited', 'Ubegrenset')}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="500"
                    step="25"
                    value={sunFilterConfig.maxDistanceKm || 500}
                    onChange={(e) => onUpdateSunFilterConfig({ ...sunFilterConfig, maxDistanceKm: Number(e.target.value) })}
                    className="w-full accent-amber-500 cursor-pointer"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Fasiliteter og egenskaper */}
          <div>
            <span className="text-3xs font-extrabold uppercase tracking-widest text-[var(--text-muted)] block mb-2">
              {t('sidebar.features_and_amenities', 'Egenskaper & Fasiliteter')}
            </span>
            <div className="space-y-1.5">
              {filterOptions.map((opt) => {
                const isSelected = activeFilters.has(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => onToggleFilter(opt.id)}
                    className={`w-full p-3 rounded-2xl border text-left flex items-center justify-between transition-all ${
                      isSelected
                        ? 'bg-brand/10 border-brand/40 shadow-sm'
                        : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] hover:border-[var(--border-secondary)]'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0 pr-2">
                      <span className="text-base shrink-0">{opt.emoji}</span>
                      <div className="truncate">
                        <p className={`text-xs font-bold leading-tight truncate ${isSelected ? 'text-emerald-400' : 'text-[var(--text-primary)]'}`}>
                          {opt.label}
                        </p>
                        <p className="text-3xs text-[var(--text-muted)] truncate mt-0.5">
                          {opt.desc}
                        </p>
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-lg flex items-center justify-center shrink-0 border transition-all ${
                      isSelected
                        ? 'bg-brand border-brand text-stone-900'
                        : 'border-[var(--border-secondary)] bg-[var(--bg-surface)] text-transparent'
                    }`}>
                      <Check size={12} strokeWidth={3} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* Footer actions */}
        <div 
          className="px-5 py-3.5 border-t border-[var(--border-primary)] bg-[var(--bg-surface)] shrink-0 flex items-center gap-3"
          style={{ paddingBottom: 'calc(var(--safe-area-bottom, 0px) + 14px)' }}
        >
          {totalActiveCount > 0 && (
            <button
              type="button"
              onClick={onClearFilters}
              className="py-2.5 px-3.5 rounded-xl border border-[var(--border-secondary)] text-[var(--text-muted)] hover:text-red-400 hover:border-red-500/30 text-xs font-bold flex items-center gap-1.5 transition-colors"
            >
              <RotateCcw size={13} />
              <span>{t('common.reset', 'Nullstill')}</span>
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-5 rounded-xl bg-brand text-stone-900 text-xs font-black uppercase tracking-wide shadow-lg hover:bg-emerald-400 transition-all active:scale-[0.98] text-center"
          >
            {matchCount > 0
              ? t('sidebar.show_matching_spots', `Vis ${matchCount} ${matchCount === 1 ? 'plass' : 'plasser'}`)
              : t('sidebar.show_spots', 'Bruk filter')}
          </button>
        </div>

      </div>
    </div>
  );
};

export default React.memo(SpotFilterModal);
